import Vue from 'vue';
import Cookie from 'js-cookie';

// localStorage é usado pros campos potencialmente grandes (permissions[] tem ~10
// slugs ou mais; cookies têm limite de 4kb compartilhado e ficam pesados).
// Cookies persistem só o essencial pro middleware do interceptor (token, id).
const LS_KEY        = 'luzrh.user';
const LS_ACTIVE_KEY = 'luzrh.active_company_id';

function readPersisted() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writePersisted(state) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({
      company_id:  state.company_id,
      company_ids: state.company_ids,
      is_company:  state.is_company,
      company:     state.company,
      companies:   state.companies,
      permissions: state.permissions,
      roles:       state.roles,
    }));
  } catch { /* quota cheia ou modo privado — ignora */ }
}

function readActive() {
  try {
    const raw = localStorage.getItem(LS_ACTIVE_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function writeActive(value) {
  try {
    if (value === null || value === undefined) localStorage.removeItem(LS_ACTIVE_KEY);
    else localStorage.setItem(LS_ACTIVE_KEY, String(value));
  } catch { /* ignore */ }
}

const persisted = readPersisted();

const initialUser = {
  id:          Cookie.get('id') || '',
  token:       Cookie.get('token') || '',
  email:       Cookie.get('email') || '',
  nome:        Cookie.get('nome') || '',
  // Campos persistidos via localStorage:
  company_id:  persisted.company_id ?? null,
  company_ids: Array.isArray(persisted.company_ids) ? persisted.company_ids : [],
  is_company:  persisted.is_company ?? false,
  company:     persisted.company ?? null,
  companies:   Array.isArray(persisted.companies) ? persisted.companies : [],
  permissions: Array.isArray(persisted.permissions) ? persisted.permissions : [],
  roles:       Array.isArray(persisted.roles) ? persisted.roles : [],
  // Filtro ativo do dropdown global (null = "Todos os CNPJs vinculados").
  active_company_id: readActive(),
};

export const user = Vue.observable(initialUser);

/**
 * Persiste e atualiza o user reactive após login/me bem-sucedido.
 * Espera o payload do UserResource (id, name, email, company_id, company_ids,
 * is_company, company, companies, roles[], permissions[]).
 */
export const setUserInfo = (payload, token) => {
  if (!payload) return;
  user.id    = payload.id    || '';
  user.token = token || user.token || '';
  user.email = payload.email || '';
  user.nome  = payload.name  || '';

  user.company_id  = payload.company_id ?? null;
  user.company_ids = Array.isArray(payload.company_ids) ? payload.company_ids : [];
  user.is_company  = !!payload.is_company;
  user.company     = payload.company ?? null;
  user.companies   = Array.isArray(payload.companies) ? payload.companies : [];
  user.permissions = Array.isArray(payload.permissions) ? payload.permissions : [];
  user.roles       = Array.isArray(payload.roles) ? payload.roles : [];

  // Se o filtro ativo persistido não está mais entre os company_ids do usuário,
  // descarta (pode ter sido removido o vínculo entre sessões).
  if (user.active_company_id && !user.company_ids.includes(user.active_company_id)) {
    user.active_company_id = null;
    writeActive(null);
  }

  // Cookies persistem só o essencial (e legível pelo backend se precisar).
  Cookie.set('id',    user.id);
  Cookie.set('nome',  user.nome);
  Cookie.set('email', user.email);
  if (token) Cookie.set('token', token);

  writePersisted(user);
};

export const removeUserInfo = () => {
  // Cookies (token + identidade básica)
  ['id', 'token', 'email', 'nome', 'roles'].forEach((key) => Cookie.remove(key));

  // localStorage com permissões e contexto de empresa
  try {
    localStorage.removeItem(LS_KEY);
    localStorage.removeItem(LS_ACTIVE_KEY);
  } catch { /* ignore */ }

  // Reset do reactive
  user.id    = '';
  user.token = '';
  user.email = '';
  user.nome  = '';
  user.company_id  = null;
  user.company_ids = [];
  user.is_company  = false;
  user.company     = null;
  user.companies   = [];
  user.permissions = [];
  user.roles       = [];
  user.active_company_id = null;
};

/**
 * Verifica se o usuário tem uma permission (slug) na sessão atual.
 * Sem argumento, retorna true (ausência de slug = sem restrição).
 */
export const hasPermission = (slug) => {
  if (!slug) return true;
  return Array.isArray(user.permissions) && user.permissions.includes(slug);
};

/**
 * Seta o CNPJ ativo do filtro global. Null = mostrar dados de todos os CNPJs vinculados.
 * Valida que o id está dentro do conjunto company_ids do user.
 */
export const setActiveCompany = (companyId) => {
  if (companyId === null || companyId === undefined) {
    user.active_company_id = null;
    writeActive(null);
    return;
  }
  const id = Number(companyId);
  if (!Number.isFinite(id) || !user.company_ids.includes(id)) {
    // id inválido: zera silenciosamente.
    user.active_company_id = null;
    writeActive(null);
    return;
  }
  user.active_company_id = id;
  writeActive(id);
};

/**
 * Retorna o filtro ativo atual (ou null pra "todos").
 */
export const getActiveCompanyId = () => user.active_company_id;

export default user;
